import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MarkdownRenderer from '../common/MarkdownRenderer';

/**
 * Explanation Modal - A reusable atom component for displaying explanations
 * 
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Function to call when closing the modal
 * @param {string} title - The title to display (e.g., section title)
 * @param {string} explanation - The explanation content (markdown supported)
 * @param {boolean} isLoading - Whether the explanation is still loading
 */
const ExplanationModal = ({
  open,
  onClose,
  title,
  explanation,
  isLoading = false
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '4px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          pb: '14px',
          pt: '14px',
          pl: 5,
          pr: '10px',
          background: 'white'
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ color: '#485869', fontSize: '1.1rem' }}>
         {title}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{
        mt: 4, py: 5, px: 5, bgcolor: 'white', maxHeight: 'calc(100vh - 300px)', overflow: 'auto',
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '8px',
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          marginTop: '5px',
          marginBottom: '5px',
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.4)',
          },
        },
        '&::-webkit-scrollbar-corner': {
          backgroundColor: 'transparent',
        },
      }}>
        <Box sx={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: '#ffffff'
        }}>
          {isLoading ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px',
              p: 6
            }}>
              <CircularProgress
                size={48}
                sx={{
                  color: '#485869',
                  mb: 2
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: '#475969',
                  fontWeight: 500,
                  fontSize: '16px',
                  mb: 1
                }}
              >
                Generating explanation...
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#6b7280',
                  fontSize: '14px'
                }}
              >
                This may take a few seconds
              </Typography>
            </Box>
          ) : (
            <Box sx={{
              backgroundColor: '#f8fafc',
              p: 3,
              borderRadius: '0px',
              border: '1px solid #e5e7eb'
            }}>
              <MarkdownRenderer content={explanation} />
            </Box>
          )}
        </Box>

        <Box sx={{
          py: 2,
          backgroundColor: '#ffffff',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              bgcolor: '#485869',
              color: 'white',
              '&:hover': {
                bgcolor: '#3a4a59',
              },
              borderRadius: '5px',
              px: 5,
              py: 0.5,
              minWidth: '120px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
            }}
          >
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ExplanationModal;